/**
 * Preview data (inside the builder's side panel). The server renders the
 * preview either with the token catalogue's platform samples or against one
 * real record of this workspace, chosen from the record picker. The client
 * never supplies sample values and never renders Liquid.
 */
import { useEffect, useRef, useState } from "react";
import { Banner, Btn, Dropdown, Input, SegmentedControl, Spinner } from "@v0y4ge/jrni-ui";
import type { MessagePreviewRecord, MessagePreviewRecordContext, MessageTemplatePreviewRecord } from "@v0y4ge/api-contracts";
import { builderApi, classifyFailure, type Failure } from "../api";
import { versionWhen, type Tr } from "../model";
import { FailureBanner } from "./common";

export type PreviewSource = { kind: "samples" } | { kind: "record"; record: MessageTemplatePreviewRecord; label: string };

export interface PreviewDataPanelProps {
  t: Tr;
  /** The record kind this message can be previewed against, or null when none is offered. */
  context: MessagePreviewRecordContext | null;
  value: PreviewSource;
  onChange: (next: PreviewSource) => void;
  /** From the latest server render: whose values the tokens took, and which had none. */
  rendered: { values: "catalog_samples" | "record"; missingTokens: string[]; unknownTokens: string[] } | null;
  rendering: boolean;
}

const recordKey = (r: MessageTemplatePreviewRecord) => JSON.stringify(r);

export function PreviewDataPanel({ t, context, value, onChange, rendered, rendering }: PreviewDataPanelProps) {
  const [q, setQ] = useState("");
  const [state, setState] = useState<{ phase: "idle" | "loading" | "ready" | "error"; context: string | null; rows: MessagePreviewRecord[]; failure: Failure | null }>({ phase: "idle", context: null, rows: [], failure: null });
  const seq = useRef(0);
  const [nonce, setNonce] = useState(0);
  const recordMode = value.kind === "record" || state.phase !== "idle";

  useEffect(() => {
    if (!context || !recordMode) return () => { seq.current++; };
    const mine = ++seq.current;
    // Rows of another record kind are never offered for this one.
    setState((s) => ({ phase: "loading", context, rows: s.context === context ? s.rows : [], failure: null }));
    const timer = setTimeout(() => {
      builderApi.listMessagePreviewRecords({ context, ...(q.trim() ? { q: q.trim() } : {}), limit: 25 }).then(
        (res) => { if (seq.current === mine) setState({ phase: "ready", context, rows: res.data, failure: null }); },
        (e: unknown) => { if (seq.current === mine) setState((s) => ({ phase: "error", context, rows: s.context === context ? s.rows : [], failure: classifyFailure(e) })); },
      );
    }, 300);
    return () => { clearTimeout(timer); seq.current++; };
  }, [context, q, nonce, recordMode]);

  const mode = value.kind === "record" || state.phase !== "idle" ? "record" : "samples";
  const options = state.rows.map((r) => ({
    value: recordKey(r.record),
    label: r.label,
    supportingText: [r.detail, r.startsAt ? versionWhen(r.startsAt) : null].filter(Boolean).join(" · ") || undefined,
  }));
  const selected = value.kind === "record" ? recordKey(value.record) : "";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--jrni-space-2)" }} data-testid="msgb-preview-data">
      <SegmentedControl<"samples" | "record"> full size="sm" ariaLabel={t("messaging.builder.previewData.mode", "Preview values")}
        value={mode}
        onChange={(m) => {
          if (m === "samples") { setState({ phase: "idle", context: null, rows: [], failure: null }); onChange({ kind: "samples" }); }
          else setState((s) => ({ ...s, phase: s.phase === "idle" ? "loading" : s.phase }));
        }}
        options={[
          { value: "samples", label: t("messaging.builder.previewData.samples", "Sample values") },
          { value: "record", label: t("messaging.builder.previewData.record", "A real record"), disabled: !context },
        ]} />
      {mode === "samples" ? (
        <p className="msgb-small">{t("messaging.builder.previewData.samplesText", "Placeholders show the platform's example values. Nothing about your customers is used.")}</p>
      ) : (
        <>
          <Input size="sm" type="search" full value={q} onChange={(e) => setQ(e.target.value)}
            aria-label={t("messaging.builder.previewData.searchLabel", "Search records")}
            placeholder={t("messaging.builder.previewData.search", "Search records")} />
          <Dropdown portal size="sm" label={t("messaging.builder.previewData.pick", "Record")}
            placeholder={state.phase === "loading" && !state.rows.length ? t("messaging.builder.previewData.loading", "Loading records…") : t("messaging.builder.previewData.choose", "Choose a record")}
            options={value.kind === "record" && !options.some((o) => o.value === selected) ? [{ value: selected, label: value.label }, ...options] : options}
            value={selected || null}
            disabled={!options.length && value.kind !== "record"}
            onChange={(v) => {
              const hit = state.rows.find((r) => recordKey(r.record) === String(v));
              if (hit) onChange({ kind: "record", record: hit.record, label: hit.label });
            }}
          />
          {state.phase === "loading" ? <span role="status" aria-live="polite" style={{ display: "inline-flex", gap: "var(--jrni-space-2)", alignItems: "center" }}><Spinner size="sm" label={t("messaging.builder.previewData.loading", "Loading records…")} /></span> : null}
          {state.phase === "error" && state.failure && state.failure.kind === "forbidden" ? (
            <Banner tone="neutral" size="compact" title={t("messaging.builder.previewData.forbidden", "You can't browse these records with your access")} testid="msgb-records-forbidden">
              {t("messaging.builder.previewData.forbiddenText", "Record access is separate from message access. Sample values still work for this preview.")}
            </Banner>
          ) : state.phase === "error" && state.failure ? <FailureBanner t={t} failure={state.failure} onRetry={() => setNonce((n) => n + 1)} /> : null}
          {state.phase === "ready" && !state.rows.length && !q ? (
            <Banner tone="info" size="compact" title={t("messaging.builder.previewData.noneYet", "No records to preview with")}>
              {t("messaging.builder.previewData.noneYetText", "This workspace has no records of this kind yet. The preview keeps using sample values.")}
            </Banner>
          ) : null}
          <p className="msgb-small">{t("messaging.builder.previewData.recordText", "The preview is rendered on the server with this record's real values, exactly as a send would. Nothing is sent.")}</p>
        </>
      )}
      {rendering ? <span className="msgb-small" role="status" aria-live="polite">{t("messaging.builder.previewData.rendering", "Updating preview…")}</span> : null}
      {rendered && rendered.missingTokens.length ? (
        <Banner tone="warning" size="compact" title={t("messaging.builder.previewData.missing", "Some placeholders had no value")}>
          {rendered.missingTokens.join(", ")}
        </Banner>
      ) : null}
      {value.kind === "record" ? (
        <Btn size="sm" variant="link" onClick={() => { setState({ phase: "idle", context: null, rows: [], failure: null }); onChange({ kind: "samples" }); }}>
          {t("messaging.builder.previewData.backToSamples", "Use sample values again")}
        </Btn>
      ) : null}
    </div>
  );
}
